import config from '../config'; // ✅ SEGURIDAD: Importación de configuración para API_BASE_URL
import { toast } from 'sonner';
import { clearRequestCache } from '../utils/requestCache';

// Cache en memoria para el token CSRF actual
let cachedCsrfToken = null;

// Promesa compartida (Singleton/Mutex) para que múltiples peticiones en paralelo compartan una única renovación de token
let refreshPromise = null;

/**
 * Obtener token CSRF para peticiones seguras
 * Si no está en caché o se invalidó, se solicita al endpoint /api/csrf-token
 */
const getCsrfToken = async () => {
  if (cachedCsrfToken) return cachedCsrfToken;

  try {
    const response = await fetch(`${config.API_BASE_URL}/api/csrf-token`, {
      credentials: 'include' // Incluir cookies para inicializar la sesión CSRF
    });
    const data = await response.json();
    cachedCsrfToken = data.csrfToken;
    return cachedCsrfToken;
  } catch (err) {
    console.error('Error al obtener CSRF token:', err);
    throw err;
  }
};

/**
 * Función auxiliar para redirigir al inicio en caso de expiración de sesión
 * Evita bucles de recarga si ya nos encontramos en la página principal o de login
 */
const redirectToLoginOrHome = () => {
  if (typeof window !== 'undefined') {
    const currentPath = window.location.pathname;
    if (currentPath !== '/' && currentPath !== '/login') {
      window.location.href = '/';
    }
  }
};

/**
 * Renovar Access Token de forma segura utilizando el Refresh Token en cookie HttpOnly
 * Utiliza un patrón Mutex para consolidar ráfagas de llamadas concurrentes en una única petición al backend
 */
const refreshAccessToken = async () => {
  // Si ya hay un refresco en curso, reutilizar la misma promesa para evitar violaciones de rotación de tokens
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      // Obtenemos el token CSRF para adjuntarlo a la petición POST de refresco
      const csrfToken = await getCsrfToken().catch(() => null);
      const headers = {
        'Content-Type': 'application/json'
      };
      if (csrfToken) {
        headers['x-csrf-token'] = csrfToken;
      }

      const refreshResponse = await fetch(`${config.API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // Enviar cookie HttpOnly con refreshToken
        headers
      });

      if (refreshResponse.ok) {
        return true;
      }

      // Si el servidor rechazó por error CSRF (403), invalidamos el token en cache
      if (refreshResponse.status === 403) {
        cachedCsrfToken = null;
      }

      return false;
    } catch (err) {
      console.error('Error en renovación de sesión:', err);
      return false;
    } finally {
      // Liberamos la promesa para futuras renovaciones
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// ✅ SEGURIDAD: Fetch wrapper que maneja cookies HttpOnly, tokens CSRF, rate limit y renovación de token
const fetchWithAuth = async (url, options = {}) => {
  // Detectar si el cuerpo es FormData para evitar sobreescribir el boundary de multipart/form-data
  const isFormData = options.body instanceof FormData;

  let headers = {
    ...options.headers,
  };

  // Solo establecer Content-Type a application/json si no se envían archivos ni FormData
  if (!isFormData && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  // ✅ SEGURIDAD: Obtener CSRF token para peticiones de escritura (POST/PUT/DELETE/PATCH)
  const isMutatingMethod = ['POST', 'PUT', 'DELETE', 'PATCH'].includes((options.method || 'GET').toUpperCase());
  if (isMutatingMethod) {
    // Una escritura deja obsoletos los GET cacheados (empresa, servicios, sucursales...): limpiar la caché (TASK-122)
    clearRequestCache();
    try {
      const csrfToken = await getCsrfToken();
      if (csrfToken) {
        headers['x-csrf-token'] = csrfToken;
      }
    } catch (csrfErr) {
      console.warn('No se pudo adjuntar token CSRF:', csrfErr);
    }
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include' // ✅ SEGURIDAD: Incluir cookies HttpOnly automáticamente
    });

    // ✅ SEGURIDAD & UX: Si el servidor retorna 429 (Too Many Requests), NO redirigir ni cerrar sesión
    if (response.status === 429) {
      console.warn('Petición limitada por el servidor (429 Too Many Requests)');
      toast.error('Demasiadas solicitudes. Esperá un momento y volvé a intentar.', {
        id: 'global-rate-limit'
      });
      return response;
    }

    // Si falló por token CSRF inválido (403), invalidar la caché para la próxima petición
    if (response.status === 403) {
      try {
        const cloned = await response.clone().json();
        if (cloned.code === 'CSRF_VALIDATION_FAILED') {
          cachedCsrfToken = null;
        }
      } catch {
        // Respuesta no JSON, ignorar
      }
    }

    // ✅ SEGURIDAD & PERSISTENCIA: Ante cualquier 401, intentar renovación silenciosa con refreshToken antes de cerrar sesión
    if (response.status === 401 && !options.skipAuthRedirect && !options._isRetry) {
      // Intentar renovar el Access Token con el Refresh Token en cookie HttpOnly mediante Mutex sincronizado
      const refreshed = await refreshAccessToken();

      if (refreshed) {
        // Reintentamos la petición original con las nuevas cookies ya establecidas marcando _isRetry
        let retryHeaders = { ...headers };
        if (isMutatingMethod) {
          const freshCsrf = await getCsrfToken().catch(() => null);
          if (freshCsrf) retryHeaders['x-csrf-token'] = freshCsrf;
        }

        const retryResponse = await fetch(url, {
          ...options,
          _isRetry: true,
          headers: retryHeaders,
          credentials: 'include'
        });
        return retryResponse;
      } else {
        // El refresco falló: la sesión expiró de forma definitiva, limpiar caché local y redirigir
        try {
          if (typeof window !== 'undefined') {
            localStorage.removeItem('auth_user_cache');
            localStorage.removeItem('auth_initialized');
            localStorage.removeItem('auth_check_done');
            sessionStorage.removeItem('auth_initialized');
          }
        } catch (_storageErr) {
          // Ignorar error si el storage está bloqueado
        }
        redirectToLoginOrHome();
        return response;
      }
    }

    return response;
  } catch (error) {
    // Si la solicitud fue cancelada por un AbortController (desmontaje de componente), no hacer nada
    if (error.name === 'AbortError' || error.name === 'CanceledError') {
      throw error;
    }
    console.error('Error en la solicitud:', error);
    throw error;
  }
};

// TASK-140: authContext reutiliza la misma renovación (con CSRF y mutex) al montar la app
export { refreshAccessToken };

export default fetchWithAuth;
